import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateComparisonTaxSettings } from '@/redux/modules/comparison';
import { ReduxState } from '@/redux/modules/types';

import { DEFAULT_SHORT_TERM_CAPITAL_GAINS_RATE, DEFAULT_LONG_TERM_CAPITAL_GAINS_RATE } from '../../constants';
import { Dispatch } from '@/redux';

export interface TaxAnalysisInputValues {
  shortTermYTDGain?: number;
  shortTermYTDLoss?: number;
  shortTermCarriedForwardLoss?: number;
  longTermYTDGain?: number;
  longTermYTDLoss?: number;
  longTermCarriedForwardLoss?: number;
  shortTermCapitalGainsRate?: number;
  longTermCapitalGainsRate?: number;
}

export const FIELD_KEYS = {
  SHORT_TERM_YTD_GAIN: 'shortTermYTDGain',
  SHORT_TERM_YTD_LOSS: 'shortTermYTDLoss',
  LONG_TERM_YTD_GAIN: 'longTermYTDGain',
  LONG_TERM_YTD_LOSS: 'longTermYTDLoss',
  SHORT_TERM_CARRIED_FORWARD_LOSS: 'shortTermCarriedForwardLoss',
  LONG_TERM_CARRIED_FORWARD_LOSS: 'longTermCarriedForwardLoss',
  SHORT_TERM_CAPITAL_GAINS_RATE: 'shortTermCapitalGainsRate',
  LONG_TERM_CAPITAL_GAINS_RATE: 'longTermCapitalGainsRate',
} as const;

export const FIELD_LABEL_KEYS = {
  [FIELD_KEYS.SHORT_TERM_YTD_GAIN]: 'Comparison.TaxAnalysis.ShortTermYTDGain',
  [FIELD_KEYS.SHORT_TERM_YTD_LOSS]: 'Comparison.TaxAnalysis.ShortTermYTDLoss',
  [FIELD_KEYS.LONG_TERM_YTD_GAIN]: 'Comparison.TaxAnalysis.LongTermYTDGain',
  [FIELD_KEYS.LONG_TERM_YTD_LOSS]: 'Comparison.TaxAnalysis.LongTermYTDLoss',
  [FIELD_KEYS.SHORT_TERM_CARRIED_FORWARD_LOSS]: 'Comparison.TaxAnalysis.ShortTermCarriedForwardLoss',
  [FIELD_KEYS.LONG_TERM_CARRIED_FORWARD_LOSS]: 'Comparison.TaxAnalysis.LongTermCarriedForwardLoss',
  [FIELD_KEYS.SHORT_TERM_CAPITAL_GAINS_RATE]: 'Comparison.TaxAnalysis.ShortTermCapitalGainsRate',
  [FIELD_KEYS.LONG_TERM_CAPITAL_GAINS_RATE]: 'Comparison.TaxAnalysis.LongTermCapitalGainsRate',
} as const;

export const useTaxAnalysisInputs = () => {
  const dispatch = useDispatch<Dispatch>();
  const activeComparison = useSelector((state: ReduxState) => state.comparison.activeComparison);
  const initialized = useRef(false);

  // Initialize defaults in Redux once
  useEffect(() => {
    if (!initialized.current && activeComparison) {
      const { taxSettings = {} } = activeComparison?.taxAnalysis || {};

      dispatch(
        updateComparisonTaxSettings(
          {
            shortTermYTDGain: undefined,
            shortTermYTDLoss: undefined,
            shortTermCarriedForwardLoss: undefined,
            longTermYTDGain: undefined,
            longTermYTDLoss: undefined,
            longTermCarriedForwardLoss: undefined,
            shortTermCapitalGainsRate: DEFAULT_SHORT_TERM_CAPITAL_GAINS_RATE * 100,
            longTermCapitalGainsRate: DEFAULT_LONG_TERM_CAPITAL_GAINS_RATE * 100,
            ...taxSettings,
          },
          true,
        ),
      );

      initialized.current = true;
    }
  }, [activeComparison, dispatch]);

  const taxSettings = activeComparison?.taxAnalysis?.taxSettings;

  const values: TaxAnalysisInputValues = {
    shortTermYTDGain: taxSettings?.shortTermYTDGain,
    shortTermYTDLoss: taxSettings?.shortTermYTDLoss,
    shortTermCarriedForwardLoss: taxSettings?.shortTermCarriedForwardLoss,
    longTermYTDGain: taxSettings?.longTermYTDGain,
    longTermYTDLoss: taxSettings?.longTermYTDLoss,
    longTermCarriedForwardLoss: taxSettings?.longTermCarriedForwardLoss,
    shortTermCapitalGainsRate: taxSettings?.shortTermCapitalGainsRate,
    longTermCapitalGainsRate: taxSettings?.longTermCapitalGainsRate,
  };

  const createSetter = useCallback(
    (key: keyof TaxAnalysisInputValues) => (value?: number) => {
      if (!activeComparison) return;
      dispatch(updateComparisonTaxSettings({ [key]: value }));
    },
    [dispatch],
  );

  return {
    values,
    setShortTermYTDGain: createSetter(FIELD_KEYS.SHORT_TERM_YTD_GAIN),
    setShortTermYTDLoss: createSetter(FIELD_KEYS.SHORT_TERM_YTD_LOSS),
    setLongTermYTDGain: createSetter(FIELD_KEYS.LONG_TERM_YTD_GAIN),
    setLongTermYTDLoss: createSetter(FIELD_KEYS.LONG_TERM_YTD_LOSS),
    setShortTermCarriedForwardLoss: createSetter(FIELD_KEYS.SHORT_TERM_CARRIED_FORWARD_LOSS),
    setLongTermCarriedForwardLoss: createSetter(FIELD_KEYS.LONG_TERM_CARRIED_FORWARD_LOSS),
    setShortTermCapitalGainsRate: createSetter(FIELD_KEYS.SHORT_TERM_CAPITAL_GAINS_RATE),
    setLongTermCapitalGainsRate: createSetter(FIELD_KEYS.LONG_TERM_CAPITAL_GAINS_RATE),
  };
};
